import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'

function formatBytes(n) {
  const bytes = Number(n) || 0
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatWhen(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function BackupSettings() {
  const { isAdmin } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [runMessage, setRunMessage] = useState('')
  const [logTail, setLogTail] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await settingsApi.getBackupStatus()
      setStatus(data)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load backup status.',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) return undefined
    load()
    return undefined
  }, [isAdmin, load])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleRunBackup() {
    setRunning(true)
    setError('')
    setRunMessage('')
    setLogTail('')
    try {
      const result = await settingsApi.runBackup()
      setRunMessage(result.message || (result.ok ? 'Backup completed.' : 'Backup failed.'))
      setLogTail(result.log_tail || '')
      if (!result.ok) {
        setError(result.message || 'Backup failed.')
      }
      await load()
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not run backup.',
      )
    } finally {
      setRunning(false)
    }
  }

  const latest = status?.latest
  const timer = status?.timer

  return (
    <SettingsShell title="Backups" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">Backups</h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Full site backups (database and attachments). Nightly timer runs around
            02:30; you can also run one now. Restore is via the server scripts — see
            operations docs.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {runMessage && !error && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {runMessage}
            {latest?.path && runMessage.toLowerCase().includes('completed') && (
              <span className="mt-1 block font-mono text-xs text-emerald-800">
                {latest.path}
              </span>
            )}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-brand">Status</h2>
              {loading && !status ? (
                <p className="mt-2 text-sm text-brand-muted">Loading…</p>
              ) : (
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-brand-muted">Latest backup</dt>
                    <dd className="font-medium text-brand">
                      {latest
                        ? formatWhen(latest.created_at || latest.modified_at)
                        : 'None yet'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-brand-muted">Latest size</dt>
                    <dd className="font-medium text-brand">
                      {latest ? formatBytes(latest.size_bytes) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-brand-muted">Archives on disk</dt>
                    <dd className="font-medium text-brand">
                      {status?.archives?.length ?? 0} (
                      {formatBytes(status?.total_bytes || 0)})
                    </dd>
                  </div>
                  <div>
                    <dt className="text-brand-muted">Nightly timer</dt>
                    <dd className="font-medium text-brand">
                      {timer?.loaded
                        ? `${timer.unit} (${timer.active || 'unknown'})`
                        : 'Not detected (install with deploy/install-backup-timer.sh)'}
                    </dd>
                  </div>
                  {timer?.next_elapse && (
                    <div className="sm:col-span-2">
                      <dt className="text-brand-muted">Next scheduled run</dt>
                      <dd className="font-medium text-brand">
                        {formatWhen(timer.next_elapse)}
                      </dd>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <dt className="text-brand-muted">Backup folders scanned</dt>
                    <dd className="mt-1 font-mono text-xs text-brand">
                      {(status?.backup_dirs || []).join(', ') || '—'}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={loading || running}
                onClick={() => load()}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                Refresh
              </button>
              <button
                type="button"
                disabled={running || !status?.can_run_backup}
                onClick={handleRunBackup}
                className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {running ? 'Running backup…' : 'Run backup now'}
              </button>
            </div>
          </div>
          {logTail && (
            <pre className="mt-4 max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
              {logTail}
            </pre>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-brand">Archives</h2>
          <p className="mt-1 text-sm text-brand-muted">
            Newest first. Restore on the server with{' '}
            <code className="rounded bg-slate-100 px-1 text-xs">scripts/restore.sh</code>.
          </p>
          {loading && !status ? (
            <p className="mt-4 text-sm text-brand-muted">Loading…</p>
          ) : !status?.archives?.length ? (
            <p className="mt-4 text-sm text-brand-muted">No backup archives found yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-brand-muted">
                    <th className="py-2 pr-4 font-medium">Created</th>
                    <th className="py-2 pr-4 font-medium">File</th>
                    <th className="py-2 pr-4 font-medium">Size</th>
                    <th className="py-2 pr-4 font-medium">Attachments</th>
                    <th className="py-2 font-medium">App</th>
                  </tr>
                </thead>
                <tbody>
                  {status.archives.map((row) => (
                    <tr key={row.path} className="border-b border-slate-100">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-brand">
                        {formatWhen(row.created_at || row.modified_at)}
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="font-medium text-brand">{row.filename}</div>
                        <div className="font-mono text-[11px] text-brand-muted">
                          {row.directory}
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        {formatBytes(row.size_bytes)}
                      </td>
                      <td className="py-2.5 pr-4">
                        {row.attachment_files != null ? row.attachment_files : '—'}
                      </td>
                      <td className="py-2.5 whitespace-nowrap">
                        {row.app_version || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SettingsShell>
  )
}
